<template>
  <div class="customer-return">
    <h2>請記錄您的付款資訊</h2>
    <div class="notice">
      已完成取號，尚未完成付款。請依下列資訊於期限內繳費，系統收到款項後才視為付款完成。
    </div>
    <div class="payment-info">
      <div>
        <strong>支付方式：</strong>{{ paymentTypeLabel }}
      </div>
      <div>
        <strong>應付金額：</strong>{{ amountDisplay }}
      </div>
      <div v-if="showSingleCode">
        <strong>{{ paymentCodeLabel }}：</strong>{{ paymentCode }}
      </div>
      <div v-if="bankCode">
        <strong>銀行代碼：</strong>{{ bankCode }}
      </div>
      <div v-if="isBarcode">
        <strong>超商條碼：</strong>
        <div class="barcode-lines">
          <div>{{ barcode1 }}</div>
          <div>{{ barcode2 }}</div>
          <div>{{ barcode3 }}</div>
        </div>
      </div>
      <div>
        <strong>繳費期限：</strong>{{ expireDateDisplay }}<span v-if="expireTime"> {{ expireTime }}</span>
      </div>
    </div>
    <button @click="onConfirm">{{ confirmText }}</button>
  </div>
</template>

<script>
/**
 * NewebPayCustomerReturn 元件
 * 解析 URL 查詢參數，顯示 ATM/超商付款資訊
 * @author Roo
 */
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { postToGhl, postToWindow } from '../utils/ghlMessaging'
import { buildSuccessResponsePayload } from '../utils/ghlEventPayloads'

export default {
  name: 'NewebPayCustomerReturn',
  setup() {
    const route = useRoute()
    const router = useRouter()

    // 取得查詢參數
    const paymentType = ref(route.query.paymentType || '')
    const amount = ref(route.query.amount || '')
    const paymentCode = ref(route.query.paymentCode || route.query.codeNo || '')
    const bankCode = ref(route.query.bankCode || '')
    const expireDate = ref(route.query.expireDate || '')
    // const expireTime = ref(route.query.expireTime || '')
    const expireTime = ref('')
    const chargeId = ref(route.query.chargeId || '')
    const barcode1 = ref(route.query.barcode1 || '')
    const barcode2 = ref(route.query.barcode2 || '')
    const barcode3 = ref(route.query.barcode3 || '')

    /**
     * 支付方式中文標籤
     */
    const paymentTypeLabel = computed(() => {
      const t = String(paymentType.value || '').toUpperCase()
      if (t === 'VACC' || t === 'ATM') return 'ATM 轉帳'
      if (t === 'BARCODE') return '超商條碼'
      if (t === 'CVS') return '超商代碼'
      return t || '未知'
    })

    /**
     * 應付金額格式化
     */
    const amountDisplay = computed(() => {
      return amount.value ? `NT$ ${amount.value}` : '未提供'
    })

    /**
     * 付款代碼標籤
     */
    const paymentCodeLabel = computed(() => {
      const t = String(paymentType.value || '').toUpperCase()
      return (t === 'VACC' || t === 'ATM') ? '虛擬帳號' : '付款代碼'
    })

    const isBarcode = computed(() => String(paymentType.value || '').toUpperCase() === 'BARCODE')
    const showSingleCode = computed(() => {
      const t = String(paymentType.value || '').toUpperCase()
      // 僅在 ATM/CVS 顯示單一代碼；條碼以三段顯示
      return (t === 'VACC' || t === 'ATM' || t === 'CVS') && !!paymentCode.value
    })

    // 是否已成功取號（離線支付代碼/條碼已取得）
    const isNumberIssued = computed(() => {
      const t = String(paymentType.value || '').toUpperCase()
      if (t === 'BARCODE') return !!(barcode1.value && barcode2.value && barcode3.value)
      if (t === 'VACC' || t === 'ATM') return !!(bankCode.value && paymentCode.value)
      if (t === 'CVS') return !!paymentCode.value
      return false
    })

    // 按鈕文字：取號成功顯示「確定」，否則為「返回商店」
    const confirmText = computed(() => (isNumberIssued.value ? '確定' : '返回商店'))

    /**
     * 繳費期限格式化
     */
    const expireDateDisplay = computed(() => {
      // 預設格式 yyyyMMdd 或 yyyy-MM-dd
      if (!expireDate.value) return '未提供'
      if (/^\d{8}$/.test(expireDate.value)) {
        // yyyyMMdd → yyyy-MM-dd
        return `${expireDate.value.slice(0,4)}-${expireDate.value.slice(4,6)}-${expireDate.value.slice(6,8)}`
      }
      return expireDate.value
    })

    /**
     * 返回商店
     */
    function goBack() {
      router.push({ path: '/' })
    }

    // 取號成功時回傳 custom_element_success_response，否則返回商店
    function onConfirm() {
      if (isNumberIssued.value) {
        const payload = buildSuccessResponsePayload({ chargeId: chargeId.value || undefined });
        try {
          if (window.opener && !window.opener.closed) {
            postToWindow(window.opener, payload);
            try { window.close(); } catch (e) { console.warn('window.close failed', e); }
            return;
          }
          postToGhl(window.parent, payload);
        } catch (error) {
          goBack();
        }
      } else {
        goBack()
      }
    }

    // 元件掛載時可進行額外處理
    onMounted(() => {
      // 可加上額外驗證或追蹤
    })

    return {
      paymentTypeLabel,
      amountDisplay,
      paymentCode,
      paymentCodeLabel,
      bankCode,
      expireDateDisplay,
      expireTime,
      isBarcode,
      barcode1,
      barcode2,
      barcode3,
      showSingleCode,
      isNumberIssued,
      confirmText,
      onConfirm,
      goBack
    }
  }
}
</script>

<style scoped>
.customer-return {
  max-width: 400px;
  margin: 40px auto;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px #eee;
  font-family: 'Segoe UI', 'Microsoft JhengHei', Arial, sans-serif;
}
.payment-info > div {
  margin-bottom: 12px;
  font-size: 1.1em;
}
.notice {
  margin: 10px 0 16px;
  color: #555;
  font-size: 0.95em;
  line-height: 1.5;
}
.barcode-lines {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  margin-top: 6px;
  font-family: monospace;
  font-size: 1.15em;
  letter-spacing: 1px;
}
button {
  margin-top: 24px;
  padding: 8px 24px;
  font-size: 1em;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
button:hover {
  background: #1565c0;
}
</style>
